/**
 * @fileOverview X Axis
 */
import * as React from 'react';
import { ReactElement, ReactNode, useLayoutEffect, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { CartesianAxis, defaultCartesianAxisProps } from './CartesianAxis';
import {
  AxisInterval,
  AxisTick,
  RenderableAxisProps,
  PresentationAttributesAdaptChildEvent,
  AxisDomain,
  ScaleType,
  AxisDomainTypeInput,
  EvaluatedAxisDomainType,
  TickProp,
  XAxisTickContentProps,
  TickItem,
} from '../util/types';
import { useAppDispatch, useAppSelector } from '../state/hooks';
import {
  addXAxis,
  replaceXAxis,
  removeXAxis,
  XAxisOrientation,
  XAxisPadding,
  XAxisSettings,
  NiceTicksAlgorithm,
} from '../state/cartesianAxisSlice';
import {
  implicitXAxis,
  selectTicksOfAxis,
  selectXAxisPosition,
  selectXAxisSettingsNoDefaults,
  selectXAxisSize,
} from '../state/selectors/axisSelectors';
import { selectAxisViewBox } from '../state/selectors/selectChartOffsetInternal';
import { useIsPanorama } from '../context/PanoramaContext';
import { RequiresDefaultProps, resolveDefaultProps } from '../util/resolveDefaultProps';
import { axisPropsAreEqual } from '../util/axisPropsAreEqual';
import { CustomScaleDefinition } from '../util/scale/CustomScaleDefinition';
import { useCartesianChartLayout } from '../context/chartLayoutContext';
import { getAxisTypeBasedOnLayout } from '../util/getAxisTypeBasedOnLayout';

interface XAxisProps<DataPointType = any, DataValueType = any> extends Omit<
  RenderableAxisProps<DataPointType, DataValueType>,
  'domain' | 'scale' | 'tick'
> {
  /**
   * The type of axis.
   *
   * `category`: Treats data as distinct values.
   * Each value is in the same distance from its neighbors, regardless of their actual numeric difference.
   *
   * `number`: Treats data as continuous range.
   * Values that are numerically closer are placed closer together on the axis.
   *
   * `auto`: the type is inferred based on the chart layout.
   *
   * @defaultValue category
   */
  type?: AxisDomainTypeInput;
  /**
   * Unique ID that represents this XAxis.
   * Required when there are multiple XAxes.
   *
   * @defaultValue 0
   */
  xAxisId?: string | number;
  /**
   * Height of the axis in pixels.
   *
   * @defaultValue 30
   */
  height?: number;
  /**
   * If set true, flips ticks around the axis line, displaying the labels inside the chart instead of outside.
   * @defaultValue false
   */
  mirror?: boolean;
  /**
   * The orientation of axis
   * @defaultValue bottom
   */
  orientation?: XAxisOrientation;
  /**
   * Specify the domain of axis when the axis is a number axis.
   *
   * If undefined, then the domain is calculated based on the data and dataKeys.
   *
   * The length of domain should be 2, and we will validate the values in domain.
   *
   * Each element in the array can be a number, 'auto', 'dataMin', 'dataMax', a string like 'dataMin - 20', 'dataMax + 100',
   * or a function that accepts a single argument and returns a number.
   *
   * If any element of domain is set to be 'auto', comprehensible scale ticks will be calculated, and the final domain of axis is generated by the ticks.
   * If a function, receives '[dataMin, dataMax]', and must return a computed domain as '[min, max]'.
   *
   * @example <XAxis type="number" domain={['dataMin', 'dataMax']} />
   * @example <XAxis type="number" domain={[0, 'dataMax']} />
   * @example <XAxis type="number" domain={['auto', 'auto']} />
   * @example <XAxis type="number" domain={[0, 'dataMax + 1000']} />
   * @example <XAxis type="number" domain={['dataMin - 100', 'dataMax + 100']} />
   * @example <XAxis type="number" domain={[dataMin => (0 - Math.abs(dataMin)), dataMax => (dataMax * 2)]} />
   * @example <XAxis type="number" domain={([dataMin, dataMax]) => { const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax)); return [-absMax, absMax]; }} />
   * @example <XAxis type="number" domain={[0, 100]} allowDataOverflow />
   */
  domain?: AxisDomain;
  /**
   * Scale function determines how data values are mapped to visual values.
   * In other words, decided the mapping between data domain and coordinate range.
   *
   * If undefined, or 'auto', the scale function is created internally according to the type of axis and data.
   *
   * You can define a custom scale, either as a string shortcut to a d3 scale, or as a complete scale definition object.
   *
   * @defaultValue auto
   * @example <XAxis scale="log" />
   * @example
   * import { scaleLog } from 'd3-scale';
   * const scale = scaleLog().base(Math.E);
   * <XAxis scale={scale} />
   */
  scale?:
    | ScaleType
    | CustomScaleDefinition
    | CustomScaleDefinition<string>
    | CustomScaleDefinition<number>
    | CustomScaleDefinition<Date>;
  /**
   * Defines how the individual label text is rendered.
   * This controls the settings for individual ticks; on a typical axis, there are multiple ticks, depending on your data.
   *
   * If you want to customize the overall axis label, use the `label` prop instead.
   *
   * Options:
   * - `false`: Do not render any tick labels.
   * - `true`: Render tick labels with default settings.
   * - `object`: An object of props to be merged into the internally calculated tick props.
   * - `ReactElement`: A custom React element to be used as the tick label.
   * - `function`: A function that returns a React element for custom rendering of tick labels.
   *
   * @defaultValue true
   */
  tick?: TickProp<XAxisTickContentProps>;
  /**
   * Ticks can be any type when the axis is the type of category
   * Ticks must be numbers when the axis is the type of number
   *
   * @see {@link https://recharts.github.io/guide/axisTicks/}
   */
  ticks?: ReadonlyArray<AxisTick>;
  /**
   * Axis padding is the distance between the edge of plot area and the first/last tick.
   *
   * @defaultValue {"left":0,"right":0}
   */
  padding?: XAxisPadding;
  /**
   * The minimum gap between two adjacent tick labels
   *
   * @defaultValue 5
   */
  minTickGap?: number;
  /**
   * If set 0, all the ticks will be shown. If set preserveStart", "preserveEnd" or "preserveStartEnd", the ticks which is to be shown or hidden will be calculated automatically.
   * @defaultValue preserveEnd
   */
  interval?: AxisInterval;
  /**
   * The margin between tick line and tick.
   */
  tickMargin?: number;
  /**
   * Specifies the font size of tick text.
   * If this prop is not specified, Recharts will read the default tick size from the DOM.
   *
   * Font size is used to calculate the width of the ticks, which in turn is used to determine how many ticks are rendered without overlapping.
   */
  fontSize?: number | string;
  /**
   * Specifies the letter spacing of ticks.
   * This is used in conjunction with fontSize to calculate the width of the ticks.
   * If this prop is not specified, Recharts will read the default letter spacing from the DOM.
   *
   * Letter spacing is used to calculate the width of the ticks, which in turn is used to determine how many ticks are rendered without overlapping.
   */
  letterSpacing?: number | string;
  /**
   * Controls how Recharts calculates "nice" tick values for this axis.
   * Options: `'none'`, `'auto'`, `'adaptive'`, `'snap125'`.
   * See {@link NiceTicksAlgorithm} for a full description of each option.
   *
   * @see {@link https://recharts.github.io/guide/axisTicks/}
   * @defaultValue 'auto'
   * @since 3.8
   */
  niceTicks?: NiceTicksAlgorithm;
}

export type Props<DataPointType = any, DataValueType = any> = Omit<
  PresentationAttributesAdaptChildEvent<TickItem, SVGTextElement>,
  'scale' | 'ref'
> &
  XAxisProps<DataPointType, DataValueType>;

function SetXAxisSettings(props: Omit<XAxisSettings, 'type'> & { type: AxisDomainTypeInput }): ReactNode {
  const dispatch = useAppDispatch();
  const prevSettingsRef = useRef<XAxisSettings | null>(null);
  const layout = useCartesianChartLayout();
  const { type: typeFromProps, ...restProps } = props;
  const evaluatedType: EvaluatedAxisDomainType | undefined = getAxisTypeBasedOnLayout(layout, 'xAxis', typeFromProps);

  const settings: XAxisSettings | undefined = useMemo(() => {
    if (evaluatedType == null) {
      return undefined;
    }
    return {
      ...restProps,
      type: evaluatedType,
    };
  }, [restProps, evaluatedType]);

  useLayoutEffect(() => {
    if (settings == null) {
      return;
    }
    if (prevSettingsRef.current === null) {
      dispatch(addXAxis(settings));
    } else if (prevSettingsRef.current !== settings) {
      dispatch(replaceXAxis({ prev: prevSettingsRef.current, next: settings }));
    }
    prevSettingsRef.current = settings;
  }, [settings, dispatch]);

  useLayoutEffect(() => {
    return () => {
      if (prevSettingsRef.current) {
        dispatch(removeXAxis(prevSettingsRef.current));
        prevSettingsRef.current = null;
      }
    };
  }, [dispatch]);

  return null;
}

const XAxisImpl = (props: PropsWithDefaults) => {
  const { xAxisId, className } = props;
  const viewBox = useAppSelector(selectAxisViewBox);
  const isPanorama = useIsPanorama();
  const axisType = 'xAxis';
  const cartesianTickItems = useAppSelector(state => selectTicksOfAxis(state, axisType, xAxisId, isPanorama));
  const axisSize = useAppSelector(state => selectXAxisSize(state, xAxisId));
  const position = useAppSelector(state => selectXAxisPosition(state, xAxisId));
  /*
   * Here we select settings from the store and prefer to use them instead of the actual props
   * so that the chart is consistent. If we used the props directly, some components will use axis settings
   * from state and some from props and because there is a render step between these two, they might be showing different things.
   * https://github.com/recharts/recharts/issues/6257
   */
  const synchronizedSettings = useAppSelector(state => selectXAxisSettingsNoDefaults(state, xAxisId));

  if (axisSize == null || position == null || synchronizedSettings == null) {
    return null;
  }

  const { dangerouslySetInnerHTML, ticks, scale: del, ...allOtherProps } = props;
  const { id, scale: del2, ...restSynchronizedSettings } = synchronizedSettings;

  return (
    <CartesianAxis
      {...allOtherProps}
      {...restSynchronizedSettings}
      x={position.x}
      y={position.y}
      width={axisSize.width}
      height={axisSize.height}
      className={clsx(`recharts-${axisType} ${axisType}`, className)}
      viewBox={viewBox}
      ticks={cartesianTickItems}
      axisType={axisType}
      axisId={xAxisId}
    />
  );
};

export const xAxisDefaultProps = {
  allowDataOverflow: implicitXAxis.allowDataOverflow,
  allowDecimals: implicitXAxis.allowDecimals,
  allowDuplicatedCategory: implicitXAxis.allowDuplicatedCategory,
  angle: implicitXAxis.angle,
  axisLine: defaultCartesianAxisProps.axisLine,
  height: implicitXAxis.height,
  hide: false,
  includeHidden: implicitXAxis.includeHidden,
  interval: implicitXAxis.interval,
  label: false,
  minTickGap: implicitXAxis.minTickGap,
  mirror: implicitXAxis.mirror,
  orientation: implicitXAxis.orientation,
  padding: implicitXAxis.padding,
  reversed: implicitXAxis.reversed,
  scale: implicitXAxis.scale,
  tick: implicitXAxis.tick,
  tickCount: implicitXAxis.tickCount,
  tickLine: defaultCartesianAxisProps.tickLine,
  tickSize: defaultCartesianAxisProps.tickSize,
  type: implicitXAxis.type,
  niceTicks: implicitXAxis.niceTicks,
  xAxisId: 0,
} as const satisfies Partial<Props>;

type PropsWithDefaults<DataPointType = any, DataValueType = any> = RequiresDefaultProps<
  Props<DataPointType, DataValueType>,
  typeof xAxisDefaultProps
>;

const XAxisSettingsDispatcher = <DataPointType, DataValueType>(outsideProps: Props<DataPointType, DataValueType>) => {
  const props: PropsWithDefaults<DataPointType, DataValueType> = resolveDefaultProps(outsideProps, xAxisDefaultProps);
  return (
    <>
      <SetXAxisSettings
        allowDataOverflow={props.allowDataOverflow}
        allowDecimals={props.allowDecimals}
        allowDuplicatedCategory={props.allowDuplicatedCategory}
        angle={props.angle}
        dataKey={props.dataKey}
        domain={props.domain}
        height={props.height}
        hide={props.hide}
        id={props.xAxisId}
        includeHidden={props.includeHidden}
        interval={props.interval}
        minTickGap={props.minTickGap}
        mirror={props.mirror}
        name={props.name}
        orientation={props.orientation}
        padding={props.padding}
        reversed={props.reversed}
        scale={props.scale}
        tick={props.tick}
        tickCount={props.tickCount}
        tickFormatter={props.tickFormatter}
        ticks={props.ticks}
        type={props.type}
        unit={props.unit}
        niceTicks={props.niceTicks}
      />
      <XAxisImpl {...props} />
    </>
  );
};

/**
 * @consumes CartesianViewBoxContext
 * @provides CartesianLabelContext
 */
export const XAxis = React.memo(XAxisSettingsDispatcher, axisPropsAreEqual) as <
  DataPointType = any,
  DataValueType = any,
>(
  props: Props<DataPointType, DataValueType>,
) => ReactElement;
// @ts-expect-error we need to set the displayName for debugging purposes

XAxis.displayName = 'XAxis';
